import {createSketchDimensionPersistentId} from '../../identity/SketchPersistentIdFactory';
import type {SketchDimensionQuantityKind} from '../SketchDimensionTypes';

/**
 * 草图尺寸身份服务。
 *
 * 设计说明：
 * 1. 尺寸持久 ID 复用阶段 1 草图身份工厂（`cad:sketch-dimension:<uuid>`），
 *    与草图/实体/关系 ID 同一命名空间纪律；
 * 2. 尺寸名称对齐 SOLIDWORKS 风格 `D1、D2…`，按草图内已占用名称取最小空缺序号，
 *    删除后的序号可复用（与 SOLIDWORKS 行为一致，名称唯一性由仓库守卫）；
 * 3. parameterId 由 dimensionId 派生并量纲类型化（02 计划 §7.1 要求），
 *    不引入第二个随机源，保证同一尺寸的参数身份稳定可推导。
 */
export class SketchDimensionIdentityService {
    /** 尺寸名称前缀。 */
    private static readonly NAME_PREFIX = 'D';

    /**
     * 分配尺寸持久 ID。
     *
     * @returns `cad:sketch-dimension:<uuid>` 格式的稳定 ID。
     */
    public allocateDimensionId(): string {
        return createSketchDimensionPersistentId();
    }

    /**
     * 在草图内分配下一个可用尺寸名称。
     *
     * @param occupiedNames 草图内已占用名称集合。
     * @returns `D<n>` 格式名称，n 为最小空缺正整数。
     */
    public allocateName(occupiedNames: ReadonlySet<string>): string {
        let index = 1;

        while (occupiedNames.has(`${SketchDimensionIdentityService.NAME_PREFIX}${index}`)) {
            index += 1;
        }

        return `${SketchDimensionIdentityService.NAME_PREFIX}${index}`;
    }

    /**
     * 派生量纲类型化参数 ID。
     *
     * @param dimensionId 尺寸持久 ID。
     * @param quantityKind 参数量纲。
     * @returns 稳定参数 ID。
     * @throws 当 `dimensionId` 为空时抛出错误。
     */
    public createParameterId(dimensionId: string, quantityKind: SketchDimensionQuantityKind): string {
        if (!dimensionId) {
            throw new Error('派生参数 ID 需要非空 dimensionId。');
        }

        return `${dimensionId}#param:${quantityKind}`;
    }
}
